/**
 * The Monitor's *Rules* tab (ADR-0100): every rule under its name, kept to one type by the filter
 * chips, and *Add rule*, which opens the rule dialog; the pencil on a rule opens it with that rule.
 * A viewer sees the list and the filter only.
 *
 * Presentational: the feature (`features/monitor`) reads the rules and passes `edit` only to a
 * reader who may change them. The filter and the dialog are this pane's own state.
 */
import { ListChecks, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import type { MonitorRuleKind } from './AlertInbox'
import { type LoadView, MonitorSection } from './MonitorSection'
import { EMPTY_DRAFT, RuleDialog, type RuleEditing, type RuleTarget } from './RuleForm'
import { draftOf, RuleKindFilter, type RuleRow, RulesList } from './RulesList'

const RULES = 'cases.monitor.rules'

export interface RulesPaneProps {
  view: LoadView<RuleRow>
  /** Null for a reader who may not change the rules (a viewer). */
  edit: RuleEditing | null
  onRetry: () => void
  /** The rule an inbox alert pointed at: shown whatever the filter, ringed. */
  highlightRuleId?: string | null
}

const NEW_RULE: RuleTarget = { ruleId: null, draft: EMPTY_DRAFT }

/** What the dialog shows, kept while it closes; each opening mounts a fresh form. */
function useRuleDialog() {
  const [state, setState] = useState({ open: false, target: NEW_RULE })
  const show = (target: RuleTarget) => setState({ open: true, target })
  const pick = (row: RuleRow) => {
    const draft = draftOf(row)
    if (row.id && draft) show({ ruleId: row.id, draft })
  }
  const onOpenChange = (open: boolean) => setState(current => ({ ...current, open }))
  return { ...state, add: () => show(NEW_RULE), pick, onOpenChange }
}

export function RulesPane({ view, edit, onRetry, highlightRuleId = null }: RulesPaneProps) {
  const { t } = useTranslation()
  const [kind, setKind] = useState<MonitorRuleKind | null>(null)
  const dialog = useRuleDialog()
  const actions = edit
    ? { onEdit: dialog.pick, onDelete: edit.onDelete, pending: edit.pending }
    : null
  const add = edit ? (
    <Button type="button" size="sm" onClick={dialog.add}>
      <Plus aria-hidden />
      {t(`${RULES}.add`)}
    </Button>
  ) : null
  return (
    <div className="flex max-w-5xl flex-col">
      <MonitorSection
        heading={{ id: 'monitor-rules', icon: ListChecks, title: t(`${RULES}.title`) }}
        action={add}
        view={view}
        copy={{
          loading: t(`${RULES}.loading`),
          failed: t(`${RULES}.loadError`),
          empty: t(`${RULES}.empty`),
        }}
        onRetry={onRetry}
      >
        {rows => {
          // A kind whose last rule was deleted falls back to every rule.
          const shown =
            !highlightRuleId && kind && rows.some(row => row.kind === kind) ? kind : null
          return (
            <>
              {rows.length > 1 ? (
                <RuleKindFilter rows={rows} value={shown} onChange={setKind} />
              ) : null}
              <RulesList
                rows={shown ? rows.filter(row => row.kind === shown) : rows}
                actions={actions}
                highlightId={highlightRuleId}
              />
            </>
          )
        }}
      </MonitorSection>
      {edit ? (
        <RuleDialog
          target={dialog.target}
          open={dialog.open}
          onOpenChange={dialog.onOpenChange}
          edit={edit}
        />
      ) : null}
    </div>
  )
}
